import React from 'react';
import { ResizeObserver } from '../../utils/resizeObserver/resizeObserver';
import { isContentVisibleEnough } from './utils/contentVisibility';
/**
 * Custom hook that detects content visibility changes and triggers callbacks accordingly.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {() => void} [params.onContentVisible] - Callback function to be called when the content becomes visible.
 * @param {() => void} [params.onContentInvisible] - Callback function to be called when the content becomes invisible.
 * @param {number} params.minVisibleHeight - Minimum content height to consider the content visible.
 *
 * @returns {Object} An object containing the `handleContentVisibilityDetection` callback function to initialize the hook.
 * @returns {Function} returns.handleContentVisibilityDetection - Function to initialize the visibility detection with the specified elements.
 *
 * @example
 * const { handleContentVisibilityDetection } = useContentVisibilityDetection({
 *   onContentVisible: () => console.log('Content is visible'),
 *   onContentInvisible: () => console.log('Content is invisible'),
 *   minVisibleHeight: 100,
 * });
 *
 */
export const useContentVisibilityDetection = ({ onContentVisible, onContentInvisible, minVisibleHeight, }) => {
    const resizeContentObserverRef = React.useRef();
    const resizeContainerObserverRef = React.useRef();
    const isContentVisibleRef = React.useRef(undefined);
    const handleContentVisibilityDetection = React.useCallback(({ content, container }) => {
        if (container && content) {
            const handleInnerContentResize = ({ container, content, }) => {
                const isContentVisible = isContentVisibleEnough({
                    content,
                    container,
                    minVisibleHeight,
                });
                if (isContentVisible !== isContentVisibleRef.current) {
                    if (isContentVisible) {
                        onContentVisible?.();
                    }
                    else {
                        onContentInvisible?.();
                    }
                }
                isContentVisibleRef.current = isContentVisible;
            };
            handleInnerContentResize({ container, content });
            resizeContentObserverRef.current = new ResizeObserver(() => {
                handleInnerContentResize({ container, content });
            });
            resizeContainerObserverRef.current = new ResizeObserver(() => {
                handleInnerContentResize({ container, content });
            });
            resizeContentObserverRef.current.observe(content);
            resizeContentObserverRef.current.observe(container);
        }
        else {
            resizeContentObserverRef.current?.disconnect();
            resizeContainerObserverRef.current?.disconnect();
        }
    }, [onContentVisible, onContentInvisible, minVisibleHeight]);
    return { handleContentVisibilityDetection };
};
//# sourceMappingURL=useContentVisibilityDetection.js.map